import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import clsx from 'clsx';
import { Check, Copy, Download, KeyRound, MessageCircle, RefreshCw, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { PhoneField } from '@/components/PhoneField';
import { SettingsCard } from '@/components/settings/SettingsPage';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { authService, TwoFactorMethod, TwoFactorSetup, TwoFactorStatus } from '@/services/auth.service';

const messageOf = (error: unknown) => (error as AxiosError<{ message?: string }>)?.response?.data?.message;
const METHOD_LABEL: Record<TwoFactorMethod, string> = { totp: 'Authenticator app', whatsapp: 'WhatsApp code' };

/** Settings → My profile: turn the second sign-in step on or off. */
export function TwoFactorCard({ phone }: { phone?: string }) {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [passwordAction, setPasswordAction] = useState<'disable' | 'codes' | null>(null);
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(() => {
    setLoadError('');
    authService.twoFactorStatus().then(setStatus).catch((e: AxiosError) => {
      // A 404 means the server predates two-step verification — the backend needs a redeploy.
      setLoadError(e?.response?.status === 404
        ? 'Two-step verification is not available on this server yet.'
        : messageOf(e) ?? 'Two-step verification settings could not be loaded.');
    });
  }, []);
  useEffect(load, [load]);

  if (!status && loadError) {
    return (
      <SettingsCard>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p role="alert" className="text-sm text-text-secondary">{loadError}</p>
          <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={load}>Retry</Button>
        </div>
      </SettingsCard>
    );
  }
  if (!status) return <div className="skeleton h-24 rounded-2xl" />;

  return (
    <SettingsCard padded={false}>
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-start">
        <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', status.enabled ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-secondary')}>
          <ShieldCheck size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-text-primary">
            {status.enabled ? 'Two-step verification is on' : 'Two-step verification is off'}
            <StatusPill status={status.enabled ? 'active' : 'inactive'} label={status.enabled ? 'On' : 'Off'} variant={status.enabled ? 'success' : 'neutral'} />
          </div>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            {status.enabled ? (
              <>
                Codes come from your <span className="font-medium text-text-secondary">{status.method === 'whatsapp' ? `WhatsApp (${status.destination})` : 'authenticator app'}</span>.
                {' '}{status.backupCodesRemaining} backup {status.backupCodesRemaining === 1 ? 'code' : 'codes'} left.
              </>
            ) : 'Anyone with your password can sign in. Turn it on to also ask for a code from your phone.'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {status.enabled ? (
            <>
              <Button variant="secondary" size="sm" iconLeft={<KeyRound size={14} />} onClick={() => setPasswordAction('codes')}>New backup codes</Button>
              <Button variant="secondary" size="sm" onClick={() => setPasswordAction('disable')}>Turn off</Button>
            </>
          ) : (
            <Button size="sm" iconLeft={<ShieldCheck size={14} />} onClick={() => setSetupOpen(true)}>Turn on</Button>
          )}
        </div>
      </div>

      {setupOpen && (
        <SetupModal
          whatsappAvailable={status.whatsappAvailable}
          phone={phone}
          onClose={() => setSetupOpen(false)}
          onEnabled={(codes) => { setSetupOpen(false); setFreshCodes(codes); load(); }}
        />
      )}
      {passwordAction && (
        <PasswordModal
          action={passwordAction}
          onClose={() => setPasswordAction(null)}
          onDone={(codes) => { setPasswordAction(null); if (codes) setFreshCodes(codes); load(); }}
        />
      )}
      {freshCodes && <BackupCodesModal codes={freshCodes} onClose={() => setFreshCodes(null)} />}
    </SettingsCard>
  );
}

function SetupModal({ whatsappAvailable, phone, onClose, onEnabled }: {
  whatsappAvailable: boolean; phone?: string; onClose: () => void; onEnabled: (codes: string[]) => void;
}) {
  const [method, setMethod] = useState<TwoFactorMethod>('totp');
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [number, setNumber] = useState(phone ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const start = async () => {
    setBusy(true); setError('');
    try {
      const s = await authService.startTwoFactorSetup(method, method === 'whatsapp' ? number : undefined);
      setSetup(s);
      if (s.method === 'whatsapp') setCooldown(s.resendAfterSeconds);
    } catch (e) { if (!(e as AxiosError)?.response) setError('Setup could not start. Check your connection and try again.'); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (!setup || cooldown > 0) return;
    setBusy(true); setError('');
    try { const r = await authService.resendTwoFactor(setup.setupId); setCooldown(r.resendAfterSeconds); toast.success('New code sent'); }
    catch (e) { setError(messageOf(e) ?? 'A new code could not be sent.'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!setup || code.length !== 6) return;
    setBusy(true); setError('');
    try {
      const r = await authService.confirmTwoFactorSetup(setup.setupId, code);
      toast.success('Two-step verification is on');
      onEnabled(r.backupCodes);
    } catch (e) { setError(messageOf(e) ?? 'That code could not be checked.'); setCode(''); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Turn on two-step verification" widthClass="max-w-lg">
      {!setup ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <MethodOption active={method === 'totp'} onClick={() => setMethod('totp')} icon={<Smartphone size={16} />}
              title="Authenticator app" body="Google or Microsoft Authenticator, 1Password… Works without signal." />
            <MethodOption active={method === 'whatsapp'} disabled={!whatsappAvailable} onClick={() => setMethod('whatsapp')} icon={<MessageCircle size={16} />}
              title="WhatsApp code" body={whatsappAvailable ? 'A new code arrives on WhatsApp each time you sign in.' : 'Not available yet — your platform has no WhatsApp code sender.'} />
          </div>
          {method === 'whatsapp' && (
            <FormField label="WhatsApp number" hint="Codes are sent here">
              <PhoneField value={number} onChange={setNumber} placeholder="50 000 0000" />
            </FormField>
          )}
          {error && <p role="alert" className="text-xs text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button loading={busy} disabled={method === 'whatsapp' && !number} onClick={start}>{method === 'whatsapp' ? 'Send code' : 'Continue'}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {setup.method === 'totp' ? (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <img src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qrSvg)}`} alt="QR code for your authenticator app"
                className="h-40 w-40 shrink-0 rounded-xl border border-border bg-white p-2" />
              <div className="min-w-0 space-y-2 text-sm text-text-secondary">
                <p>Scan this QR code with your authenticator app, then enter the 6-digit code it shows.</p>
                <p className="text-xs text-text-muted">Can't scan? Enter this key in the app instead:</p>
                <CopyText value={setup.secret} display={setup.secret.replace(/(.{4})/g, '$1 ').trim()} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-text-secondary">
              We sent a 6-digit code to WhatsApp <span className="font-medium text-text-primary">{setup.destination}</span>.{' '}
              {cooldown > 0
                ? <span className="text-text-muted">Resend in 0:{String(cooldown).padStart(2, '0')}</span>
                : <button type="button" onClick={resend} disabled={busy} className="font-medium text-primary hover:underline disabled:opacity-50">Resend code</button>}
            </p>
          )}
          <FormField label="6-digit code" error={error || undefined}>
            <Input value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus className="h-11 font-mono tracking-[0.3em]"
              onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }} />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setSetup(null); setCode(''); setError(''); }}>Back</Button>
            <Button loading={busy} disabled={code.length !== 6} onClick={confirm}>Turn on</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MethodOption({ active, disabled, onClick, icon, title, body }: {
  active: boolean; disabled?: boolean; onClick: () => void; icon: React.ReactNode; title: string; body: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active}
      className={clsx('rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        active ? 'border-primary bg-primary-soft/40' : 'border-border hover:bg-surface-alt')}>
      <div className="flex items-center gap-2 text-sm font-semibold text-text-primary"><span className="text-primary">{icon}</span>{title}</div>
      <p className="mt-1 text-xs leading-relaxed text-text-muted">{body}</p>
    </button>
  );
}

function PasswordModal({ action, onClose, onDone }: { action: 'disable' | 'codes'; onClose: () => void; onDone: (codes?: string[]) => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setBusy(true); setError('');
    try {
      if (action === 'disable') { await authService.disableTwoFactor(password); toast.success('Two-step verification is off'); onDone(); }
      else { const r = await authService.renewBackupCodes(password); onDone(r.backupCodes); }
    } catch (e) { setError(messageOf(e) ?? 'That did not work. Try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={action === 'disable' ? 'Turn off two-step verification' : 'Make new backup codes'}>
      <p className="mb-3 text-sm text-text-secondary">
        {action === 'disable' ? 'Sign-in will ask only for your password again.' : 'Your old backup codes stop working as soon as the new ones are made.'}
      </p>
      <FormField label="Current password" error={error || undefined}>
        <Input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && password) submit(); }} />
      </FormField>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!password} onClick={submit}>{action === 'disable' ? 'Turn off' : 'Make new codes'}</Button>
      </div>
    </Modal>
  );
}

function BackupCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const [saved, setSaved] = useState(false);
  const text = codes.join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([`Slightor backup codes — each works once\n\n${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'slightor-backup-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Modal open onClose={() => saved && onClose()} title="Save your backup codes">
      <p className="mb-3 text-sm text-text-secondary">If you lose your phone, each of these codes signs you in once. They are shown only now.</p>
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-surface-alt p-3 font-mono text-sm text-text-primary">
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={() => { navigator.clipboard?.writeText(text); toast.success('Codes copied'); }}>Copy</Button>
        <Button variant="secondary" size="sm" iconLeft={<Download size={13} />} onClick={download}>Download</Button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I saved these codes somewhere safe
      </label>
      <div className="mt-4 flex justify-end"><Button disabled={!saved} onClick={onClose}>Done</Button></div>
    </Modal>
  );
}

function CopyText({ value, display }: { value: string; display: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface-alt px-3 py-2 text-left font-mono text-xs text-text-primary hover:bg-surface">
      <span className="break-all">{display}</span>
      {copied ? <Check size={13} className="shrink-0 text-success" /> : <Copy size={13} className="shrink-0 text-text-muted" />}
    </button>
  );
}

export const twoFactorMethodLabel = (method?: TwoFactorMethod) => (method ? METHOD_LABEL[method] : undefined);
